import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button, SelectableCard, Skeleton } from "@akuna/ui";
import { toast } from "@/hooks/use-toast";
import { useEntitlements } from "@/hooks/useEntitlements";
import {
  useMemberPermissions, useResetMemberPermissions, useSetMemberPermission, useSetMemberRole, useTeamFeatures,
} from "@/hooks/useTeam";
import { useHasPermissionSync } from "@/hooks/usePermissions";
import { teamErrorKey } from "@/lib/team/api";
import { TEAM_ROLES, type TeamAction } from "@/lib/team/catalog";
import type { TeamMemberDetail } from "@/lib/team/types";
import { PermissionMatrix } from "./PermissionMatrix";
import { buildMatrix } from "./permissionMatrixModel";

export function MemberPermissionsTab({ member }: { member: TeamMemberDetail }) {
  const { t } = useTranslation("team");
  const { hasPermission: canUpdate } = useHasPermissionSync("team", "edit");
  const { data: perms, isLoading } = useMemberPermissions(member.user_id);
  const { data: features = [] } = useTeamFeatures();
  const { data: entitlements } = useEntitlements();
  const setRole = useSetMemberRole();
  const setPermission = useSetMemberPermission();
  const reset = useResetMemberPermissions();

  const locked = member.is_owner || member.is_self || !canUpdate;
  const activeModules = useMemo(
    () => new Set<string>(["core", ...(entitlements?.activeModules ?? [])]),
    [entitlements?.activeModules],
  );
  const groups = useMemo(
    () => (perms ? buildMatrix(features, perms.effective, activeModules) : []),
    [features, perms, activeModules],
  );

  const run = async (action: Promise<void>, success: string) => {
    try {
      await action;
      toast({ title: success });
    } catch (error) {
      toast({ title: t(teamErrorKey(error)), variant: "destructive" });
    }
  };

  const onToggle = (feature: string, action: TeamAction, next: boolean) =>
    run(setPermission.mutateAsync({ userId: member.user_id, feature, action, allowed: next }), t("permissions.permissionChanged"));

  return (
    <div className="flex flex-col gap-6">
      {(member.is_owner || member.is_self) && (
        <p className="rounded-md bg-state-warning-soft px-4 py-3 text-sm text-state-warning-foreground">
          {member.is_owner ? t("data.ownerLocked") : t("permissions.selfLocked")}
        </p>
      )}

      <section className="flex flex-col gap-4 rounded-card border border-border bg-card p-6" aria-labelledby="team-role">
        <div className="flex flex-col gap-1">
          <h2 id="team-role" className="text-base font-semibold">{t("permissions.roleTitle")}</h2>
          <p className="text-sm text-muted-foreground">{t("permissions.roleDescription")}</p>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {(member.is_owner ? (["owner"] as const) : TEAM_ROLES).map((role) => (
            <SelectableCard
              key={role}
              title={t(`roles.${role}.name`)}
              description={t(`roles.${role}.description`)}
              selected={member.role_value === role}
              disabled={locked || setRole.isPending}
              onSelect={() =>
                role !== "owner" && role !== member.role_value &&
                run(setRole.mutateAsync({ userId: member.user_id, role }), t("permissions.roleChanged", { role: t(`roles.${role}.name`) }))
              }
            />
          ))}
        </div>
      </section>

      <section className="overflow-hidden rounded-card border border-border bg-card" aria-labelledby="team-matrix">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-border px-6 py-5">
          <div className="flex flex-col gap-1">
            <h2 id="team-matrix" className="text-base font-semibold">{t("permissions.matrixTitle")}</h2>
            <p className="text-sm text-muted-foreground">{t("permissions.matrixDescription")}</p>
          </div>
          {!locked && (perms?.override_count ?? 0) > 0 && (
            <Button variant="outline" onClick={() => run(reset.mutateAsync(member.user_id), t("permissions.resetDone"))}>
              {t("permissions.reset")}
            </Button>
          )}
        </div>
        <div className="flex flex-wrap gap-5 border-b border-border px-6 py-3.5 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-2"><span className="h-6 w-6 rounded-sm bg-primary" aria-hidden="true" />{t("permissions.legendOn")}</span>
          <span className="inline-flex items-center gap-2"><span className="h-6 w-6 rounded-sm border border-border bg-card" aria-hidden="true" />{t("permissions.legendOff")}</span>
          <span className="inline-flex items-center gap-2"><span className="inline-flex h-6 w-6 items-center justify-center" aria-hidden="true">–</span>{t("permissions.legendNa")}</span>
        </div>
        {isLoading ? <Skeleton className="m-6 h-64" label={t("member.loading")} /> : <PermissionMatrix groups={groups} disabled={locked || setPermission.isPending} onToggle={onToggle} />}
      </section>
    </div>
  );
}
